import type { DashboardUpcomingItem, DateInputValue } from "../types/dashboard.types";
import { isSameCalendarDay, toDateMillis, toDateValue, toDayKey } from "./dashboardDate.utils";
import { normalizeStatusValue } from "./dashboardStatus.utils";
import { eventClassroomUuids, eventScope } from "../../schoolCalendar/utils";

export const normalizeList = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  const record = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  if (Array.isArray(record.content)) return record.content as T[];
  if (Array.isArray(record.items)) return record.items as T[];
  if (Array.isArray(record.data)) return record.data as T[];
  if (Array.isArray(record.events)) return record.events as T[];
  return [];
};

export const readRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

export const readString = (record: Record<string, unknown>, keys: string[], fallback = ""): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return fallback;
};

export const getDateField = (record: Record<string, unknown>, keys: string[]): DateInputValue => {
  for (const key of keys) {
    if (record[key] != null) return record[key] as DateInputValue;
  }
  return null;
};

export const getSlotStartValue = (slot: unknown): DateInputValue => {
  const record = readRecord(slot);
  const selectedSlot = readRecord(record.selectedSlot);
  const nestedSlot = readRecord(record.slot);
  return (
    getDateField(record, ["bookedSlotStart", "booked_slot_start", "bookedStartTime", "booked_start_time", "startsAt", "startTime", "startDateTime", "start", "slotStart", "dateTime"]) ??
    getDateField(selectedSlot, ["startsAt", "startTime"]) ??
    getDateField(nestedSlot, ["startsAt", "startTime"])
  );
};

export const getSlotEndValue = (slot: unknown): DateInputValue => {
  const record = readRecord(slot);
  const selectedSlot = readRecord(record.selectedSlot);
  const nestedSlot = readRecord(record.slot);
  return (
    getDateField(record, ["bookedSlotEnd", "booked_slot_end", "bookedEndTime", "booked_end_time", "endsAt", "endTime", "endDateTime", "end", "slotEnd"]) ??
    getDateField(selectedSlot, ["endsAt", "endTime"]) ??
    getDateField(nestedSlot, ["endsAt", "endTime"])
  );
};

export const normalizeMeetingTitle = (value: unknown): string =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[–—−]/g, "-")
    .replace(/oudergesprekken/g, "oudergesprek")
    .replace(/parent meetings/g, "parent meeting")
    .replace(/[^A-Za-z0-9\s-]/g, " ")
    .replace(/\s*-\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CANCELLED_PARENT_MEETING_STATUSES = new Set(["CANCELLED", "CANCELED", "ANNULLED", "DELETED", "EXPIRED", "REJECTED", "DECLINED", "INACTIVE"]);
const NOT_BOOKED_PARENT_MEETING_STATUSES = new Set(["AVAILABLE", "OPEN", "FREE", "RESERVED", "PENDING", "INVITED", "DRAFT"]);
const BOOKED_PARENT_MEETING_STATUSES = new Set(["BOOKED", "CONFIRMED", "PLANNED", "SCHEDULED"]);

const statusMatches = (status: string, statusSet: Set<string>) =>
  statusSet.has(status) || Array.from(statusSet).some((blocked) => status.startsWith(`${blocked}_`) || status.endsWith(`_${blocked}`));

const getParentMeetingStatusValues = (meeting: unknown): string[] => {
  const record = readRecord(meeting);
  const selectedSlot = readRecord(record.selectedSlot);
  const slot = readRecord(record.slot);
  const invitation = readRecord(record.invitation);
  const booking = readRecord(record.booking);
  return [
    record.status,
    record.invitationStatus,
    record.bookingStatus,
    record.selectedSlotStatus,
    record.slotStatus,
    selectedSlot.status,
    slot.status,
    invitation.status,
    booking.status,
  ]
    .map(normalizeStatusValue)
    .filter(Boolean);
};

export const isInactiveStatus = (status: string): boolean => statusMatches(status, CANCELLED_PARENT_MEETING_STATUSES);

export const isCancelledParentMeeting = (meeting: unknown): boolean => {
  const record = readRecord(meeting);
  if (!meeting) return true;
  if (record.cancelled === true || record.canceled === true || record.deleted === true) return true;
  if (record.active === false) return true;
  return getParentMeetingStatusValues(meeting).some(isInactiveStatus);
};

const hasParentMeetingBookedIdentity = (meeting: unknown): boolean => {
  const record = readRecord(meeting);
  return Boolean(record.childName || record.childFullName || record.studentName || record.pupilName || record.childUuid || record.parentName || record.parentEmail || record.parentEmailConfirmed || record.parentEmailOriginal || record.parentUuid || record.invitationId || record.bookingId);
};

export const isBookedParentMeeting = (meeting: unknown): boolean => {
  if (!meeting || isCancelledParentMeeting(meeting)) return false;
  const start = toDateValue(getSlotStartValue(meeting));
  const end = toDateValue(getSlotEndValue(meeting));
  if (!start || !end || end.getTime() <= start.getTime()) return false;
  const statuses = getParentMeetingStatusValues(meeting);
  if (statuses.some((status) => statusMatches(status, NOT_BOOKED_PARENT_MEETING_STATUSES))) return false;
  if (statuses.some((status) => statusMatches(status, BOOKED_PARENT_MEETING_STATUSES))) return true;
  return statuses.length === 0 && hasParentMeetingBookedIdentity(meeting);
};

export const extractParentMeetingChildName = (title: unknown, fallback = ""): string => {
  const normalizedFallback = normalizeMeetingTitle(fallback);
  if (normalizedFallback) return normalizedFallback;
  return normalizeMeetingTitle(title)
    .replace(/\boudergesprek\b/g, " ")
    .replace(/\bparent meeting\b/g, " ")
    .replace(/\bwith\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const isParentMeetingTitle = (title: unknown): boolean => {
  const normalized = normalizeMeetingTitle(title);
  return normalized.includes("oudergesprek") || normalized.includes("parent meeting");
};

export const isInactiveCalendarEvent = (event: unknown): boolean => {
  const record = readRecord(event);
  if (!event) return true;
  if (record.cancelled === true || record.canceled === true || record.deleted === true) return true;
  if (record.active === false) return true;
  return [record.status, record.eventStatus, record.calendarStatus].map(normalizeStatusValue).filter(Boolean).some(isInactiveStatus);
};

// Shared by the dashboard "Upcoming" panel and the Agenda & activities page, so both use one definition.
export const isPersonalCalendarEvent = (event: unknown, classroomIds: Set<string>): boolean => {
  if (isInactiveCalendarEvent(event)) return false;
  if (eventScope(event) === "SCHOOL") return true;
  return eventClassroomUuids(event).some((id: unknown) => classroomIds.has(String(id)));
};

export const buildParentMeetingDedupeKey = ({ title, childName, start }: { title: unknown; childName?: string; start: DateInputValue }): string => {
  const time = toDateMillis(start);
  const childKey = extractParentMeetingChildName(title, childName);
  return `parent-meeting-${toDayKey(time)}-${childKey || normalizeMeetingTitle(title)}`;
};

export const rangesOverlapOrTouch = (left: Pick<DashboardUpcomingItem, "date" | "endDate">, right: Pick<DashboardUpcomingItem, "date" | "endDate">): boolean => {
  const leftStart = toDateValue(left.date);
  const leftEnd = toDateValue(left.endDate) || leftStart;
  const rightStart = toDateValue(right.date);
  const rightEnd = toDateValue(right.endDate) || rightStart;
  return Boolean(leftStart && leftEnd && rightStart && rightEnd && leftStart.getTime() <= rightEnd.getTime() && rightStart.getTime() <= leftEnd.getTime());
};

export const shouldDedupeUpcomingItems = (existing: DashboardUpcomingItem, item: DashboardUpcomingItem): boolean =>
  Boolean(existing.parentMeetingDedupeKey && existing.parentMeetingDedupeKey === item.parentMeetingDedupeKey && rangesOverlapOrTouch(existing, item));

export const mergeUpcomingItems = (existing: DashboardUpcomingItem, item: DashboardUpcomingItem): DashboardUpcomingItem => {
  if (existing.source === "calendar" && item.source === "parentMeeting") {
    return { ...existing, details: { ...existing.details, ...item.details, raw: existing.details.raw || item.details.raw } };
  }
  if (existing.source === "parentMeeting" && item.source === "calendar") {
    return { ...item, details: { ...item.details, ...existing.details, raw: item.details.raw || existing.details.raw } };
  }
  return existing.time <= item.time ? existing : item;
};

export const dedupeAndSortUpcoming = (upcoming: DashboardUpcomingItem[]): DashboardUpcomingItem[] =>
  upcoming
    .sort((a, b) => a.time - b.time)
    .reduce<DashboardUpcomingItem[]>((items, item) => {
      const matchingIndex = items.findIndex((existing) => {
        if (shouldDedupeUpcomingItems(existing, item)) return true;
        const key = item.dedupeKey || `${item.type}-${item.time}-${item.title}`;
        const existingKey = existing.dedupeKey || `${existing.type}-${existing.time}-${existing.title}`;
        return key === existingKey;
      });
      if (matchingIndex === -1) return [...items, item];
      const next = [...items];
      next[matchingIndex] = mergeUpcomingItems(next[matchingIndex], item);
      return next;
    }, [])
    .sort((a, b) => a.time - b.time);

export const isFutureOrToday = (value: DateInputValue, today = new Date()): boolean => {
  const day = new Date(today);
  day.setHours(0, 0, 0, 0);
  const time = toDateMillis(value);
  return Number.isFinite(time) && time >= day.getTime();
};

export const isSameDay = isSameCalendarDay;
